import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Heading,
    Text,
    Button,
    Select,
    FormControl,
    FormLabel,
    Spinner,
    Alert,
    AlertIcon,
    VStack,
    HStack,
    Collapse,
    useDisclosure,
    Progress,
    Flex,
    Badge,
    useToast,
    Grid,
    GridItem,
    IconButton
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { FaFilePdf } from 'react-icons/fa';

const API_BASE_URL = "https://qaapirgeneng.digitalfields.co.za/ws1.cfc";

const BiographicsDashboard = () => {
    const [selectedLevels, setSelectedLevels] = useState([
        "10", "11", "12", "13"  // All report levels selected by default
    ]);
    const [selectedMonths, setSelectedMonths] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [biographicsData, setBiographicsData] = useState(null);
    const toast = useToast();

    // Accordion state for biographics categories
    const [genderOpen, setGenderOpen] = useState(true);
    const [ethnicityOpen, setEthnicityOpen] = useState(true);
    const [ageOpen, setAgeOpen] = useState(true);
    const [qualificationOpen, setQualificationOpen] = useState(true);

    const HuserID = localStorage.getItem("HuserID");
    const UUID = localStorage.getItem("UUID");

    const reportLevelOptions = [
        { value: "10", label: "Grade A - B: Leading the Business" },
        { value: "11", label: "Grade C - D: Leading the Function" },
        { value: "12", label: "Grade E - F: Leading Others" },
        { value: "13", label: "Grade G: Leading Self" }
    ];

    // Generate months starting from October 2024
    const generateMonthOptions = () => {
        const months = [];
        const startDate = new Date(2024, 9, 1); // October 2024 (month 9 = October, 0-indexed)
        const currentDate = new Date();
        
        // Calculate how many months from October 2024 to current month
        const monthsDiff = (currentDate.getFullYear() - startDate.getFullYear()) * 12 + 
                          (currentDate.getMonth() - startDate.getMonth());
        
        for (let i = 0; i <= monthsDiff; i++) {
            const date = new Date(startDate.getFullYear(), startDate.getMonth() + i, 1);
            const monthName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
            const monthValue = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            
            months.push({
                value: monthValue,
                label: monthName,
                date: date
            });
        }
        
        return months;
    };

    const monthOptions = generateMonthOptions();

    // Set default selected months (current month + last 2 months = 3 months total)
    useEffect(() => {
        const defaultMonths = monthOptions.slice(-3).map(month => month.value);
        setSelectedMonths(defaultMonths);
    }, []);

    const buildApiUrl = useCallback((levels, months) => {
        const reportLevelList = levels.join(',');
        let url = `${API_BASE_URL}?method=DashboardBio&huserID=${HuserID}&uuid=${UUID}&reportlevellist=${reportLevelList}`;
        
        if (months.length > 0) {
            const startMonth = months[0];
            const endMonth = months[months.length - 1];
            url += `&startMonth=${startMonth}&endMonth=${endMonth}`;
        }
        
        return url;
    }, [HuserID, UUID]);

    const fetchDashboardData = useCallback(async () => {
        if (selectedLevels.length === 0) {
            setError('Please select at least one report level.');
            return;
        }

        if (selectedMonths.length === 0) {
            setError('Please select at least one reporting period.');
            return;
        }

        try {
            setLoading(true);
            setError('');
            
            const endpoint = buildApiUrl(selectedLevels, selectedMonths);
            const cacheBustingUrl = `${endpoint}&_t=${Date.now()}`;
            
            const response = await fetch(cacheBustingUrl);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data && data.length > 0) {
                setBiographicsData(data[0]); // Store the first item from the response
                
                toast({
                    title: "Biographics Report Generated",
                    description: "Report data loaded successfully.",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                setError('No biographics data found for the selected criteria.');
            }
            
        } catch (error) {
            console.error('Error generating biographics report:', error);
            setError(error.message);
            toast({
                title: "Error generating biographics report",
                description: error.message,
                status: "error",
                duration: 6000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    }, [selectedLevels, selectedMonths, buildApiUrl, toast]);

    const handleLevelChange = (event) => {
        const value = event.target.value;
        setSelectedLevels(prev => {
            if (prev.includes(value)) {
                return prev.filter(level => level !== value);
            } else {
                return [...prev, value];
            }
        });
    };

    const handleMonthClick = (monthValue) => {
        const monthIndex = monthOptions.findIndex(m => m.value === monthValue);
        
        if (selectedMonths.includes(monthValue)) {
            // If clicking on a selected month, check if we can deselect it while maintaining sequence
            const selectedIndices = selectedMonths.map(m => monthOptions.findIndex(opt => opt.value === m)).sort((a, b) => a - b);
            
            // Only allow deselection if it's at the start or end of the selection
            if (monthIndex === selectedIndices[0] || monthIndex === selectedIndices[selectedIndices.length - 1]) {
                setSelectedMonths(prev => prev.filter(m => m !== monthValue));
            }
        } else {
            // If clicking on an unselected month, extend the selection to include it
            if (selectedMonths.length === 0) {
                setSelectedMonths([monthValue]);
            } else {
                const selectedIndices = selectedMonths.map(m => monthOptions.findIndex(opt => opt.value === m)).sort((a, b) => a - b);
                const minSelected = selectedIndices[0];
                const maxSelected = selectedIndices[selectedIndices.length - 1];
                
                let newStartIndex, newEndIndex;
                
                if (monthIndex < minSelected) {
                    // Extend backwards
                    newStartIndex = monthIndex;
                    newEndIndex = maxSelected;
                } else if (monthIndex > maxSelected) {
                    // Extend forwards
                    newStartIndex = minSelected;
                    newEndIndex = monthIndex;
                } else {
                    // Fill gaps (shouldn't happen with sequential requirement, but just in case)
                    newStartIndex = Math.min(minSelected, monthIndex);
                    newEndIndex = Math.max(maxSelected, monthIndex);
                }
                
                const newSelectedMonths = [];
                for (let i = newStartIndex; i <= newEndIndex; i++) {
                    newSelectedMonths.push(monthOptions[i].value);
                }
                setSelectedMonths(newSelectedMonths);
            }
        }
    };

    // Handle PDF generation
    const handleGeneratePDF = () => {
        if (!biographicsData) {
            toast({
                title: "No data available",
                description: "Please generate a report first before creating PDF.",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        try {
            // Build the PDF URL with the same parameters
            const reportLevelList = selectedLevels.join(',');
            const startMonth = selectedMonths[0];
            const endMonth = selectedMonths[selectedMonths.length - 1];
            
            const pdfUrl = `https://qaapirgeneng.digitalfields.co.za/htmltemplates/transnetBiographicsReport.cfm?huserID=${HuserID}&uuid=${UUID}&reportlevellist=${reportLevelList}&startMonth=${startMonth}&endMonth=${endMonth}`;
            
            // Use fetch to get the PDF as blob and then download it
            fetch(pdfUrl)
                .then(response => response.blob())
                .then(blob => {
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = 'biographics-report.pdf';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    
                    toast({
                        title: "PDF Report Generated",
                        description: "PDF report is being downloaded.",
                        status: "success",
                        duration: 3000,
                        isClosable: true,
                    });
                })
                .catch(error => {
                    console.error('Error downloading PDF:', error);
                    toast({
                        title: "Error downloading PDF",
                        description: error.message,
                        status: "error",
                        duration: 6000,
                        isClosable: true,
                    });
                });
            
        } catch (error) {
            console.error('Error generating PDF:', error);
            toast({
                title: "Error generating PDF",
                description: error.message,
                status: "error",
                duration: 6000,
                isClosable: true,
            });
        }
    };

    return (
        <Box p={8} maxW="100%" overflowX="auto">
            <Heading mb={6} fontSize="2xl">Biographics Dashboard</Heading>
            
            {/* Report Level Selection and Reporting Period Selection */}
            <Grid templateColumns="1fr 1fr" gap={6} mb={8}>
                {/* Report Level Selection */}
                <GridItem>
                    <Box p={6} bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" h="100%">
                        <FormControl mb={6}>
                            <FormLabel fontWeight="bold" fontSize="lg">Select Report Levels:</FormLabel>
                            <VStack align="start" spacing={3}>
                                {reportLevelOptions.map(option => (
                                    <HStack key={option.value} spacing={3}>
                                        <input
                                            type="checkbox"
                                            value={option.value}
                                            checked={selectedLevels.includes(option.value)}
                                            onChange={handleLevelChange}
                                            style={{ transform: 'scale(1.2)' }}
                                        />
                                        <Text fontSize="md">{option.label}</Text>
                                    </HStack>
                                ))}
                            </VStack>
                        </FormControl>
                    </Box>
                </GridItem>

                {/* Reporting Period Selection */}
                <GridItem>
                    <Box p={6} bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" h="100%">
                        <FormControl mb={6}>
                            <FormLabel fontWeight="bold" fontSize="lg">Select Reporting Period:</FormLabel>
                            <Text fontSize="sm" color="gray.600" mb={4}>
                                Click months to select a sequential range. Default: Last 3 months including current.
                            </Text>
                            <Grid templateColumns="repeat(3, 1fr)" gap={2}>
                                {monthOptions.map(month => (
                                    <Button
                                        key={month.value}
                                        size="sm"
                                        variant={selectedMonths.includes(month.value) ? "solid" : "outline"}
                                        colorScheme={selectedMonths.includes(month.value) ? "orange" : "gray"}
                                        onClick={() => handleMonthClick(month.value)}
                                        fontSize="xs"
                                        p={2}
                                        height="auto"
                                        whiteSpace="normal"
                                        textAlign="center"
                                    >
                                        {month.label}
                                    </Button>
                                ))}
                            </Grid>
                        </FormControl>
                    </Box>
                </GridItem>
            </Grid>

            {/* Generate Report Button */}
            <Box textAlign="center" mb={8}>
                <Button 
                    colorScheme="blue" 
                    size="lg"
                    onClick={fetchDashboardData}
                    isLoading={loading}
                    loadingText="Generating..."
                    isDisabled={selectedLevels.length === 0 || selectedMonths.length === 0}
                >
                    Generate Report
                </Button>
            </Box>

            {/* Loading State */}
            {loading && (
                <Box textAlign="center" py={12}>
                    <Spinner size="xl" />
                    <Text mt={4} fontSize="lg">Loading biographics data...</Text>
                </Box>
            )}

            {/* Error State */}
            {error && !loading && (
                <Alert status="error" mb={6} fontSize="md">
                    <AlertIcon />
                    {error}
                </Alert>
            )}

            {/* Content */}
            {!loading && !error && biographicsData && (
                <Box 
                    border="1px solid" 
                    borderColor="gray.300" 
                    borderRadius="lg" 
                    p={6} 
                    bg="white"
                    boxShadow="sm"
                >
                    {/* Report Levels Display and Reporting Period Display with PDF Icon */}
                    {biographicsData.ReportLevelListArray && biographicsData.ReportLevelListArray.length > 0 && (
                        <Grid templateColumns="1fr 1fr auto" gap={8} mb={8} alignItems="start">
                            {/* Report Levels Display - Left side */}
                            <GridItem>
                                <Text fontWeight="bold" mb={3} fontSize="lg">Report for levels:</Text>
                                <VStack align="start" spacing={2}>
                                    {biographicsData.ReportLevelListArray.map((level, index) => (
                                        <Text key={index} fontSize="md">
                                            {level.name}
                                        </Text>
                                    ))}
                                </VStack>
                            </GridItem>
                            
                            {/* Reporting Period Display - Right side */}
                            <GridItem>
                                {(() => {
                                    // Get reporting period from headerArray
                                    let reportStartDate = '';
                                    let reportEndDate = '';
                                    
                                    if (biographicsData.headerArray && biographicsData.headerArray.length > 0) {
                                        reportStartDate = biographicsData.headerArray[0].reportStartDate || '';
                                        reportEndDate = biographicsData.headerArray[0].reportEndDate || '';
                                    }
                                    
                                    return (
                                        <>
                                            <Text fontWeight="bold" mb={3} fontSize="lg">Reporting period:</Text>
                                            <VStack align="start" spacing={2}>
                                                <Text fontSize="md">
                                                    <Text as="span" fontWeight="semibold">Start date:</Text> {reportStartDate}
                                                </Text>
                                                <Text fontSize="md">
                                                    <Text as="span" fontWeight="semibold">End date:</Text> {reportEndDate}
                                                </Text>
                                            </VStack>
                                        </>
                                    );
                                })()}
                            </GridItem>

                            {/* PDF Icon - Top right */}
                            <GridItem>
                                <IconButton
                                    aria-label="Generate PDF Report"
                                    icon={<FaFilePdf />}
                                    colorScheme="red"
                                    size="lg"
                                    onClick={handleGeneratePDF}
                                    title="Generate PDF Report"
                                    _hover={{
                                        bg: "red.600",
                                        transform: "scale(1.05)"
                                    }}
                                    transition="all 0.2s"
                                />
                            </GridItem>
                        </Grid>
                    )}

                    {/* Biographics Categories */}
                    <Box mb={8}>
                        <Text fontWeight="bold" mb={6} fontSize="lg">Biographics:</Text>
                        
                        {/* Population Count */}
                        {biographicsData.headerArray && biographicsData.headerArray.length > 0 && (
                            <Text fontSize="md" fontWeight="bold" color="gray.700" mb={6}>
                                Number of reports in this collection: {biographicsData.headerArray[0].populationCount || 0}
                            </Text>
                        )}
                        
                        {/* Gender Array */}
                        {biographicsData.genderArray && biographicsData.genderArray.length > 0 && (
                            <Box 
                                border="1px solid" 
                                borderColor="gray.200" 
                                borderRadius="md" 
                                bg="gray.50" 
                                mb={6}
                            >
                                <Box 
                                    p={4} 
                                    bg="gray.100" 
                                    borderRadius="md" 
                                    cursor="pointer" 
                                    onClick={() => setGenderOpen(!genderOpen)}
                                    _hover={{ bg: "gray.200" }}
                                >
                                    <Flex justify="space-between" align="center">
                                        <Text fontWeight="bold" fontSize="lg">
                                            Gender
                                        </Text>
                                        {genderOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                    </Flex>
                                </Box>
                                
                                <Collapse in={genderOpen}>
                                    <Box p={6}>
                                        <VStack align="stretch" spacing={4}>
                                            {biographicsData.genderArray.map((item, index) => (
                                                <HStack key={index} spacing={4}>
                                                    <Text fontSize="sm" minW="120px" textAlign="right">
                                                        {item.tgender}:
                                                    </Text>
                                                    <Box w="400px">
                                                        <Progress 
                                                            value={Math.min(item.perc, 100)} 
                                                            size="lg"
                                                            bg="gray.200"
                                                            height="30px"
                                                            title={`Count: ${item.ccnt}`}
                                                            sx={{
                                                                '& > div[role="progressbar"]': {
                                                                    backgroundColor: '#3182CE' // Blue color for all bars
                                                                }
                                                            }}
                                                        />
                                                    </Box>
                                                    <Text fontSize="sm" fontWeight="bold" minW="60px">
                                                        {item.perc.toFixed(1)}%
                                                    </Text>
                                                </HStack>
                                            ))}
                                        </VStack>
                                    </Box>
                                </Collapse>
                            </Box>
                        )}

                        {/* Ethnicity Array */}
                        {biographicsData.ethnicityArray && biographicsData.ethnicityArray.length > 0 && (
                            <Box 
                                border="1px solid" 
                                borderColor="gray.200" 
                                borderRadius="md" 
                                bg="gray.50" 
                                mb={6}
                            >
                                <Box 
                                    p={4} 
                                    bg="gray.100" 
                                    borderRadius="md" 
                                    cursor="pointer" 
                                    onClick={() => setEthnicityOpen(!ethnicityOpen)}
                                    _hover={{ bg: "gray.200" }}
                                >
                                    <Flex justify="space-between" align="center">
                                        <Text fontWeight="bold" fontSize="lg">
                                            Ethnicity
                                        </Text>
                                        {ethnicityOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                    </Flex>
                                </Box>
                                
                                <Collapse in={ethnicityOpen}>
                                    <Box p={6}>
                                        <VStack align="stretch" spacing={4}>
                                            {biographicsData.ethnicityArray.map((item, index) => (
                                                <HStack key={index} spacing={4}>
                                                    <Text fontSize="sm" minW="120px" textAlign="right">
                                                        {item.tethnicity}:
                                                    </Text>
                                                    <Box w="400px">
                                                        <Progress 
                                                            value={Math.min(item.perc, 100)} 
                                                            size="lg"
                                                            bg="gray.200"
                                                            height="30px"
                                                            title={`Count: ${item.ccnt}`}
                                                            sx={{
                                                                '& > div[role="progressbar"]': {
                                                                    backgroundColor: '#3182CE' // Blue color for all bars
                                                                }
                                                            }}
                                                        />
                                                    </Box>
                                                    <Text fontSize="sm" fontWeight="bold" minW="60px">
                                                        {item.perc.toFixed(1)}%
                                                    </Text>
                                                </HStack>
                                            ))}
                                        </VStack>
                                    </Box>
                                </Collapse>
                            </Box>
                        )}

                        {/* Qualification Array */}
                        {biographicsData.qualificationArray && biographicsData.qualificationArray.length > 0 && (
                            <Box 
                                border="1px solid" 
                                borderColor="gray.200" 
                                borderRadius="md" 
                                bg="gray.50" 
                                mb={6}
                            >
                                <Box 
                                    p={4} 
                                    bg="gray.100" 
                                    borderRadius="md" 
                                    cursor="pointer" 
                                    onClick={() => setQualificationOpen(!qualificationOpen)}
                                    _hover={{ bg: "gray.200" }}
                                >
                                    <Flex justify="space-between" align="center">
                                        <Text fontWeight="bold" fontSize="lg">
                                            Qualification Level
                                        </Text>
                                        {qualificationOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                    </Flex>
                                </Box>
                                
                                <Collapse in={qualificationOpen}>
                                    <Box p={6}>
                                        <VStack align="stretch" spacing={4}>
                                            {biographicsData.qualificationArray.map((item, index) => (
                                                <HStack key={index} spacing={4}>
                                                    <Text fontSize="sm" minW="120px" textAlign="right">
                                                        {item.tqualificationlevel}:
                                                    </Text>
                                                    <Box w="400px">
                                                        <Progress 
                                                            value={Math.min(item.perc, 100)} 
                                                            size="lg"
                                                            bg="gray.200"
                                                            height="30px"
                                                            title={`Count: ${item.ccnt}`}
                                                            sx={{
                                                                '& > div[role="progressbar"]': {
                                                                    backgroundColor: '#3182CE' // Blue color for all bars
                                                                }
                                                            }}
                                                        />
                                                    </Box>
                                                    <Text fontSize="sm" fontWeight="bold" minW="60px">
                                                        {item.perc.toFixed(1)}%
                                                    </Text>
                                                </HStack>
                                            ))}
                                        </VStack>
                                    </Box>
                                </Collapse>
                            </Box>
                        )}

                        {/* Age Array */}
                        {biographicsData.ageArray && biographicsData.ageArray.length > 0 && (
                            <Box 
                                border="1px solid" 
                                borderColor="gray.200" 
                                borderRadius="md" 
                                bg="gray.50" 
                                mb={6}
                            >
                                <Box 
                                    p={4} 
                                    bg="gray.100" 
                                    borderRadius="md" 
                                    cursor="pointer" 
                                    onClick={() => setAgeOpen(!ageOpen)}
                                    _hover={{ bg: "gray.200" }}
                                >
                                    <Flex justify="space-between" align="center">
                                        <Text fontWeight="bold" fontSize="lg">
                                            Age
                                        </Text>
                                        {ageOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                    </Flex>
                                </Box>
                                
                                <Collapse in={ageOpen}>
                                    <Box p={6}>
                                        <VStack align="stretch" spacing={4}>
                                            {biographicsData.ageArray.map((item, index) => (
                                                <HStack key={index} spacing={4}>
                                                    <Text fontSize="sm" minW="120px" textAlign="right">
                                                        {item.ageYears} years:
                                                    </Text>
                                                    <Box w="400px">
                                                        <Progress 
                                                            value={Math.min(item.perc, 100)} 
                                                            size="lg"
                                                            bg="gray.200"
                                                            height="30px"
                                                            title={`Count: ${item.ccnt}`}
                                                            sx={{
                                                                '& > div[role="progressbar"]': {
                                                                    backgroundColor: '#3182CE' // Blue color for all bars
                                                                }
                                                            }}
                                                        />
                                                    </Box>
                                                    <Text fontSize="sm" fontWeight="bold" minW="60px">
                                                        {item.perc.toFixed(1)}%
                                                    </Text>
                                                </HStack>
                                            ))}
                                        </VStack>
                                    </Box>
                                </Collapse>
                            </Box>
                        )}
                    </Box>
                </Box>
            )}

            {/* Initial State Message */}
            {!loading && !error && !biographicsData && (
                <Alert status="info" fontSize="md">
                    <AlertIcon />
                    Please select report levels and reporting period, then click "Generate Report" to view the biographics dashboard.
                </Alert>
            )}
        </Box>
    );
};

export default BiographicsDashboard;


